import { CONSUMER_LOAN_LIMITS } from "shared/CalculatorLimits";
import { CONSUMER_LOAN_PURPOSES } from "shared/CalculatorReasons";
import { getMonthlyInstalment } from "shared/getMonthlyInstalment";
import { useConsumerLoanSettingsQuery } from "web_myplann/Api/Calculators/useConsumerLoanSettingsQuery";
import { CalculatorNote } from "web_myplann/Calculators/Components/CalculatorNote";
import { CalculatorRateAmount } from "web_myplann/Calculators/Components/CalculatorRateAmount";
import { CalculatorResultSection } from "web_myplann/Calculators/Components/CalculatorResultSection";
import { CalculatorShell } from "web_myplann/Calculators/Components/CalculatorShell";
import { CONSUMER_LOAN_DEFAULT_YEARS } from "web_myplann/Calculators/constants";
import { getConsumerLoanFormSchema } from "web_myplann/Calculators/Forms/consumerLoanFormSchema";
import { useCalculatorForm } from "web_myplann/Calculators/Hooks/useCalculatorForm";
import { useCalculatorLead } from "web_myplann/Calculators/Hooks/useCalculatorLead";
import { useReasonOptions } from "web_myplann/Calculators/Hooks/useReasonOptions";
import { useYearsLabel } from "web_myplann/Calculators/Hooks/useYearsLabel";
import { CalculatorType } from "web_myplann/Calculators/Types/CalculatorType";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getCurrencySymbol } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { SliderFormRow } from "web_myplann/Shared/Components/SliderFormRow";
import { CurrencyCode } from "web_myplann/Shared/constants";

export const ConsumerLoanCalculatorPage = () => {
	const { t } = useResource();
	const yearsLabel = useYearsLabel();
	const {
		data: settings,
		isPending: isSettingsPending,
		isError: isSettingsError,
	} = useConsumerLoanSettingsQuery();
	const { send, ...lead } = useCalculatorLead(CalculatorType.ConsumerLoan);
	const { options: purposeOptions, getLeadValue } = useReasonOptions(
		CONSUMER_LOAN_PURPOSES,
		Resources.Calculators.ConsumerLoan.Purposes,
	);
	const { control, watch, handleSubmit } = useCalculatorForm(
		getConsumerLoanFormSchema,
		{ years: CONSUMER_LOAN_DEFAULT_YEARS },
	);

	const { loanValue, years } = watch();
	const interestRate = settings?.interest;
	const hasValidLoan =
		loanValue >= CONSUMER_LOAN_LIMITS.loanMin &&
		loanValue <= CONSUMER_LOAN_LIMITS.loanMax;
	const monthlyPayment =
		hasValidLoan && interestRate !== undefined
			? getMonthlyInstalment(interestRate, years, loanValue)
			: 0;
	const isResultLoading = hasValidLoan && isSettingsPending;

	const onSubmit = handleSubmit((values) => {
		if (interestRate === undefined) {
			return;
		}

		send({
			amount: values.loanValue,
			installment: monthlyPayment,
			interest: interestRate,
			period: values.years,
			purpose: getLeadValue(values.purpose),
		});
	});

	return (
		<CalculatorShell
			type={CalculatorType.ConsumerLoan}
			onSubmit={onSubmit}
			fields={
				<>
					<FlatRowFormInput
						control={control}
						name="loanValue"
						label={t(Resources.Calculators.ConsumerLoan.LoanValue)}
						placeholder={t(Resources.Calculators.FillPlaceholder)}
						type="number"
						max={CONSUMER_LOAN_LIMITS.loanMax}
						suffix={getCurrencySymbol(CurrencyCode.CZK)}
					/>
					<FlatRowFormSelect
						control={control}
						name="purpose"
						label={t(Resources.Calculators.ConsumerLoan.Purpose)}
						options={purposeOptions}
						placeholder={t(Resources.Calculators.SelectPlaceholder)}
					/>
					<SliderFormRow
						control={control}
						name="years"
						label={t(Resources.Calculators.ConsumerLoan.Years)}
						formatValue={yearsLabel}
						min={CONSUMER_LOAN_LIMITS.yearsMin}
						max={CONSUMER_LOAN_LIMITS.yearsMax}
						isLast
					/>
				</>
			}
			isResultLoading={isResultLoading}
			result={
				monthlyPayment > 0 &&
				interestRate !== undefined && (
					<CalculatorResultSection
						badge={t(Resources.Calculators.Result.Badges.monthlyInstalment)}
						hero={
							<CalculatorRateAmount
								amount={monthlyPayment}
								rate={interestRate}
							/>
						}
						lead={lead}
					/>
				)
			}
		>
			{hasValidLoan && isSettingsError && (
				<CalculatorNote
					text={t(Resources.Calculators.Result.RatesUnavailable)}
				/>
			)}
		</CalculatorShell>
	);
};
